import React, { useEffect, useState } from "react";
import { Seo } from "../components/Seo";
import { useNavigate, useParams } from "react-router-dom";
import { api, auth } from "../lib/api";
import { TotalReachLogo } from "../components/Logo";
import { Gift, TicketPercent, Coins, Loader2, CheckCircle2, XCircle, ArrowRight, Wallet, LogIn } from "lucide-react";

const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const KIND = {
  gift_card: { label: "eGift Card", icon: Gift },
  coupon: { label: "Coupon", icon: TicketPercent },
  campaign_credit: { label: "Campaign Credit", icon: Coins },
};
const field = "w-full bg-slate-900/60 border border-white/10 rounded-md px-4 py-3 text-white focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 focus:outline-none transition-colors";
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };

function CardFace({ card }) {
  const meta = KIND[card.kind] || KIND.gift_card;
  const Icon = meta.icon;
  const value = card.value_type === "percent" ? `${card.value}% off` : usd(card.remaining_value ?? card.value);
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-cyan-400/15 via-slate-900 to-blue-500/10 p-6" data-testid="claim-card-face">
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gradient-to-br from-cyan-400/20 to-blue-500/20 blur-2xl" />
      <div className="flex items-center justify-between relative">
        <TotalReachLogo size={24} />
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-cyan-200 border border-cyan-400/30 rounded-full px-2.5 py-1"><Icon className="w-3 h-3" /> {meta.label}</span>
      </div>
      <div className="mt-8 font-display text-5xl font-bold brand-gradient" data-testid="claim-card-value">{value}</div>
      <div className="mt-2 text-sm text-slate-300" data-testid="claim-card-name">{card.promotion?.name}</div>
      {card.promotion?.description && <div className="mt-1 text-xs text-slate-400">{card.promotion.description}</div>}
      <div className="mt-6 flex items-center justify-between text-[11px] text-slate-400 font-mono relative">
        <span data-testid="claim-card-code">{card.code}</span>
        <span>{card.expires_at ? `Expires ${card.expires_at.slice(0, 10)}` : "No expiration"}</span>
      </div>
    </div>
  );
}

export default function ClaimPromo() {
  const { code } = useParams();
  const navigate = useNavigate();
  const user = auth.getUser();
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", organization: "", password: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get(`/public/promotions/claim/${encodeURIComponent(code)}`).then((r) => setCard(r.data))
      .catch((e) => setError(errMsg(e, "This code could not be found")));
  }, [code]);

  const claimLoggedIn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/claim", { code });
      setResult({ kind: "wallet", card: data.card, already: data.result === "already_yours" });
    } catch (e) { setError(errMsg(e, "Could not claim this code")); } finally { setBusy(false); }
  };

  const signupAndClaim = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) { setError("Name, email and a password are required"); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/public/access/request", {
        type: "signup", name: form.name, email: form.email, organization: form.organization,
        password: form.password, promo_code: code,
      });
      setResult({ kind: "signup", message: data.message, claim: data.claim });
    } catch (e) { setError(errMsg(e, "Sign-up failed — try again")); } finally { setBusy(false); }
  };

  const claimable = card && card.status === "issued" && !card.expired && card.promotion?.active;
  const blockedReason = card && !claimable ? (
    card.status === "claimed" ? "This code has already been claimed." :
    card.status === "redeemed" ? "This code has already been used." :
    card.status === "void" ? "This code was cancelled by the issuer." :
    card.expired ? `This code expired on ${card.expires_at?.slice(0, 10)}.` :
    !card.promotion?.active ? "This promotion is no longer active." : null) : null;

  return (
    <div className="min-h-screen bg-app text-white flex flex-col" data-testid="claim-page">
      <Seo title="Claim your campaign credit" noindex />
      <header className="px-6 py-5 flex items-center justify-between max-w-5xl mx-auto w-full">
        <button onClick={() => navigate("/")} data-testid="claim-logo-home"><TotalReachLogo size={30} /></button>
        {!user && <button onClick={() => navigate(`/login?next=/claim/${code}`)} className="text-sm text-slate-300 hover:text-cyan-300 inline-flex items-center gap-1.5" data-testid="claim-login-link"><LogIn className="w-4 h-4" /> Log in</button>}
      </header>
      <main className="flex-1 flex items-start justify-center px-6 pb-16 pt-6">
        <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-300 font-medium mb-3">TotalReach Promotions</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">You've got campaign credit.</h1>
            <p className="mt-4 text-slate-300 text-sm leading-relaxed">
              Claim it into your TotalReach wallet and it's applied automatically the next time you approve &amp; launch an ad campaign.
              Promotional credit never applies to fundraising contributions.
            </p>
            <div className="mt-8">
              {!card && !error && <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Looking up your code…</div>}
              {card && <CardFace card={card} />}
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6 lg:mt-14" data-testid="claim-action-panel">
            {error && !card && (
              <div className="text-center py-6" data-testid="claim-not-found">
                <XCircle className="w-10 h-10 text-rose-300 mx-auto" />
                <div className="mt-3 font-display text-xl font-bold">Code not found</div>
                <p className="text-sm text-slate-400 mt-1">{error}</p>
              </div>
            )}

            {result?.kind === "wallet" && (
              <div className="text-center py-4" data-testid="claim-success-wallet">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto" />
                <div className="mt-3 font-display text-2xl font-bold">{result.already ? "Already in your wallet" : "Added to your wallet"}</div>
                <p className="text-sm text-slate-400 mt-2">It'll be applied automatically at your next campaign launch.</p>
                <button onClick={() => navigate("/billing")} className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900" data-testid="claim-open-wallet-btn">
                  <Wallet className="w-4 h-4" /> Open my wallet
                </button>
              </div>
            )}

            {result?.kind === "signup" && (
              <div className="text-center py-4" data-testid="claim-success-signup">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto" />
                <div className="mt-3 font-display text-2xl font-bold">Account created</div>
                <p className="text-sm text-slate-300 mt-3 leading-relaxed">{result.message}</p>
                {result.claim?.result === "failed" && <p className="text-xs text-amber-300 mt-3" data-testid="claim-signup-warning">Credit note: {String(result.claim.reason)}</p>}
                <button onClick={() => navigate("/login")} className="mt-6 text-sm text-cyan-300 hover:underline" data-testid="claim-goto-login">Go to login →</button>
              </div>
            )}

            {card && !result && blockedReason && (
              <div className="text-center py-6" data-testid="claim-blocked">
                <XCircle className="w-10 h-10 text-amber-300 mx-auto" />
                <div className="mt-3 font-display text-xl font-bold">Can't be claimed</div>
                <p className="text-sm text-slate-400 mt-1">{blockedReason}</p>
                {user && <button onClick={() => navigate("/billing")} className="mt-5 text-sm text-cyan-300 hover:underline" data-testid="claim-blocked-wallet">View my wallet →</button>}
              </div>
            )}

            {card && !result && claimable && user && (
              <div data-testid="claim-logged-in">
                <div className="font-display text-xl font-bold">Add to your wallet</div>
                <p className="text-sm text-slate-400 mt-1">Signed in as <span className="text-white">{user.email}</span>.</p>
                {error && <div className="mt-3 text-sm text-rose-300" data-testid="claim-error">{error}</div>}
                <button onClick={claimLoggedIn} disabled={busy} data-testid="claim-now-btn"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 hover:opacity-90 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />} Claim {card.value_type === "percent" ? `${card.value}% off` : usd(card.value)}
                </button>
              </div>
            )}

            {card && !result && claimable && !user && (
              <div data-testid="claim-signup-form">
                <div className="font-display text-xl font-bold">Create your account &amp; claim</div>
                <p className="text-sm text-slate-400 mt-1">Your credit is bound to this account the moment you sign up. An administrator approves new accounts before first sign-in.</p>
                <div className="mt-5 space-y-3">
                  <input className={field} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="claim-name" />
                  <input className={field} type="email" placeholder="Work email" value={form.email} onChange={set("email")} data-testid="claim-email" />
                  <input className={field} placeholder="Campaign / organization (optional)" value={form.organization} onChange={set("organization")} data-testid="claim-org" />
                  <input className={field} type="password" placeholder="Choose a password (8+ chars)" value={form.password} onChange={set("password")} data-testid="claim-password" />
                </div>
                {error && <div className="mt-3 text-sm text-rose-300" data-testid="claim-error">{error}</div>}
                <button onClick={signupAndClaim} disabled={busy} data-testid="claim-signup-btn"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 hover:opacity-90 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Sign up &amp; claim credit
                </button>
                <div className="mt-4 text-center text-xs text-slate-500">
                  Already have an account? <button onClick={() => navigate(`/login?next=/claim/${code}`)} className="text-cyan-300 hover:underline" data-testid="claim-switch-login">Log in to claim</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
